import { Clock, CheckCircle2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateQuickSelect } from '@/components/ui/date-quick-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { useCreateTimelog } from '@/hooks'
import { cn } from '@/lib/utils'

interface TimelogPromptDialogProps {
  /** 是否打开 */
  open: boolean
  /** 关闭回调 */
  onOpenChange: (open: boolean) => void
  /** 任务 ID */
  issueId: string
  /** 任务标题 */
  issueTitle: string
  /** 任务编号 */
  issueCode?: string
  /** 成功回调 */
  onSuccess?: () => void
  /** 跳过回调 */
  onSkip?: () => void
}

/**
 * 任务完成后的工时记录提示对话框
 * 当任务状态更新为"已完成"时弹出，提示用户记录工时
 */
export function TimelogPromptDialog({
  open,
  onOpenChange,
  issueId,
  issueTitle,
  issueCode,
  onSuccess,
  onSkip,
}: TimelogPromptDialogProps) {
  const [formData, setFormData] = useState({
    hours: 1,
    minutes: 0,
    logDate: new Date().toISOString().split('T')[0],
    description: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateTimelog()
  const toast = useToast()

  const resetForm = () => {
    setFormData({
      hours: 1,
      minutes: 0,
      logDate: new Date().toISOString().split('T')[0],
      description: '',
    })
    setErrors({})
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.logDate) {
      newErrors.logDate = '请选择日期'
    } else {
      const logDate = new Date(formData.logDate)
      const today = new Date()
      today.setHours(23, 59, 59, 999)
      if (logDate > today) {
        newErrors.logDate = '日期不能是未来'
      }
    }

    const totalMinutes = formData.hours * 60 + formData.minutes
    if (totalMinutes <= 0) {
      newErrors.time = '工时必须大于0'
    } else if (totalMinutes > 1440) {
      newErrors.time = '工时不能超过24小时'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const totalMinutes = formData.hours * 60 + formData.minutes

    createMutation.mutate(
      {
        issueId,
        logDate: formData.logDate,
        minutes: totalMinutes,
        description: formData.description || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            '工时已记录',
            `已为 ${issueTitle} 记录 ${formData.hours}h ${formData.minutes}m`
          )
          resetForm()
          onOpenChange(false)
          onSuccess?.()
        },
        onError: (error) => {
          toast.error('记录失败', error.message || '请稍后重试')
        },
      }
    )
  }

  const handleSkip = () => {
    resetForm()
    onOpenChange(false)
    onSkip?.()
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <DialogTitle>任务已完成</DialogTitle>
              <DialogDescription>
                是否记录此任务的工时？
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* 任务信息 */}
          <div className="mb-4 rounded-md bg-muted p-3">
            <div className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                {issueCode && (
                  <span className="text-sm text-muted-foreground">{issueCode} </span>
                )}
                <span className="text-sm font-medium">{issueTitle}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* 工时输入 */}
            <div>
              <Label className="mb-2 block">
                工时 <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="24"
                  value={formData.hours}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hours: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={cn('w-20', errors.time && 'border-destructive')}
                />
                <span className="text-sm text-muted-foreground">小时</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minutes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minutes: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={cn('w-20', errors.time && 'border-destructive')}
                />
                <span className="text-sm text-muted-foreground">分钟</span>
              </div>
              {errors.time && (
                <p className="mt-1 text-sm text-destructive">{errors.time}</p>
              )}
            </div>

            {/* 日期选择 */}
            <div>
              <Label className="mb-2 block">
                日期 <span className="text-destructive">*</span>
              </Label>
              <Input
                type="date"
                value={formData.logDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, logDate: e.target.value }))
                }
                max={new Date().toISOString().split('T')[0]}
                className={cn(errors.logDate && 'border-destructive')}
              />
              {/* 日期快捷选项 (Requirements: 11.6) - 工时记录只能选择今天 */}
              <DateQuickSelect
                value={formData.logDate}
                onChange={(date) =>
                  setFormData((prev) => ({ ...prev, logDate: date }))
                }
                className="mt-2"
                options={{
                  includeToday: true,
                  includeTomorrow: false,
                  includeNextMonday: false,
                }}
              />
              {errors.logDate && (
                <p className="mt-1 text-sm text-destructive">{errors.logDate}</p>
              )}
            </div>

            {/* 描述 */}
            <div>
              <Label className="mb-2 block">描述（可选）</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="完成了什么工作..."
                rows={2}
                maxLength={500}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={createMutation.isPending}
            >
              跳过
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? '记录中...' : '记录工时'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Hook: 管理任务完成后的工时提示状态
 */
export function useTimelogPrompt() {
  const [promptState, setPromptState] = useState<{
    open: boolean
    issueId: string
    issueTitle: string
    issueCode?: string
  }>({
    open: false,
    issueId: '',
    issueTitle: '',
    issueCode: undefined,
  })

  const showPrompt = (issue: { id: string; title: string; code?: string }) => {
    setPromptState({
      open: true,
      issueId: issue.id,
      issueTitle: issue.title,
      issueCode: issue.code,
    })
  }

  const hidePrompt = () => {
    setPromptState((prev) => ({ ...prev, open: false }))
  }

  return {
    promptState,
    showPrompt,
    hidePrompt,
    setPromptOpen: (open: boolean) => setPromptState((prev) => ({ ...prev, open })),
  }
}
